
import React, { useState } from 'react';
import { Upload, Check, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeader } from "@/services/authService";

export interface UploadedFile {
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Date;
}

const RootFileUploader = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Load existing files on component mount
  React.useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetch('/api/admin/root-files', {
          headers: {
            ...getAuthHeader(),
          },
        });

        if (!response.ok) {
          console.error("Error fetching root files:", response.statusText);
          return;
        }

        const data = await response.json();
        const fileList: UploadedFile[] = data.map((item: any) => ({
          name: item.name,
          url: item.url || '',
          size: item.size || 0,
          type: item.type || '',
          uploadedAt: item.uploadedAt ? new Date(item.uploadedAt) : new Date(),
        }));

        setUploadedFiles(fileList);
      } catch (err) {
        console.error("Error in domain files fetch:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/admin/root-files', {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(`Upload error: ${errorBody.error || response.statusText}`);
      }

      const copyData = await response.json();

      // Add the newly uploaded file to the list
      const newFile: UploadedFile = {
        name: file.name,
        url: copyData.url || '',
        size: copyData.size || file.size,
        type: copyData.type || file.type,
        uploadedAt: new Date(),
      };

      setUploadedFiles(prev => [newFile, ...prev]);
      
      toast({
        title: "File uploaded successfully",
        description: `${file.name} is now accessible at your domain root`,
        // Change from 'success' to 'default' as toast only supports 'default' or 'destructive'
        variant: "default"
      });

      // Reset the file input
      setFile(null);
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    try {
      // Call edge function to delete the file from the domain bucket
      const response = await fetch(`/api/admin/root-files/${encodeURIComponent(fileName)}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeader(),
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(`Delete error: ${errorBody.error || response.statusText}`);
      }

      // Remove from the list
      setUploadedFiles(prev => prev.filter(f => f.name !== fileName));
      
      toast({
        title: "File deleted",
        description: `${fileName} has been removed from your domain root`,
      });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Domain Root File Uploader</CardTitle>
        <CardDescription>
          Upload files that will be accessible at your domain root (e.g., yourdomain.com/filename.ext)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="grid gap-4">
            <div className="flex items-center gap-4">
              <Input 
                id="fileInput"
                type="file" 
                onChange={handleFileChange}
                className="flex-1"
                disabled={isUploading}
              />
              <Button 
                onClick={handleUpload} 
                disabled={!file || isUploading}
                className="bg-elfGreen hover:bg-elfGreen-dark"
              >
                {isUploading ? 'Uploading...' : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Upload to Root
                  </>
                )}
              </Button>
            </div>
            {file && (
              <div className="text-sm text-gray-500">
                Selected: {file.name} ({formatFileSize(file.size)})
              </div>
            )}
          </div>

          <div className="border rounded-md">
            <div className="bg-muted px-4 py-2 border-b">
              <h3 className="font-medium">Files at Domain Root</h3>
            </div>
            <div className="divide-y">
              {isLoading ? (
                <div className="flex justify-center items-center p-6">
                  <p>Loading files...</p>
                </div>
              ) : uploadedFiles.length === 0 ? (
                <div className="flex justify-center items-center p-6">
                  <p className="text-gray-500">No files uploaded yet</p>
                </div>
              ) : (
                uploadedFiles.map((item) => (
                  <div key={item.name} className="px-4 py-3 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                        <span>{formatFileSize(item.size)}</span>
                        <span>•</span>
                        <span>{new Date(item.uploadedAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        asChild
                      >
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => handleDelete(item.name)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="text-sm text-gray-500">
        Files uploaded here will be publicly accessible at your domain root.
      </CardFooter>
    </Card>
  );
};

export default RootFileUploader;
